
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { User, Product, Look, WardrobeItem, Gender, Category, SubscriptionTier, HomeLayout } from './types';
import { withApiOrigin } from "./utils/withApiOrigin";

const ENABLE_DB_SYNC = ((import.meta as any)?.env?.VITE_ENABLE_DB_SYNC || "").toString() === "1";


interface AppState {
  user: User | null;
  products: Product[];
  wardrobe: WardrobeItem[];
  looks: Look[];
  homeLayout: HomeLayout;
  loading: boolean;
  aiBusy: boolean;
  aiError: string | null;
  actions: {
    login: (emailOrUsername: string, password: string) => Promise<void>;
    register: (email: string, username: string, password: string) => Promise<void>;
    startPhoneAuth: (phone: string) => Promise<void>;
    verifyPhoneAuth: (phone: string, code: string, referralCode?: string) => Promise<any>;
    updateProfileSizes: (sizeTop: string, sizeBottom: string, sizeShoes: string, catalogGenderPreference?: string) => Promise<void>;
    updatePublicProfile: (publicSlug: string, publicDisplayName: string, publicBio: string, publicSocialUrl: string) => Promise<void>;
    refreshMe: () => Promise<User | null>;
    logout: () => Promise<void>;
    resetSession: () => void;
    toggleHomeLayout: () => void;
    addToWardrobe: (product: Product) => void;
    addMultipleToWardrobe: (products: Product[]) => void;
    upsertWardrobeItem: (item: WardrobeItem) => void;
    removeFromWardrobe: (id: string) => void;
    createLook: (items: WardrobeItem[]) => Promise<string | undefined>;
    setSelfie: (url: string) => void;
    likeLook: (id: string) => void;
    reactToLook: (id: string, reaction?: 'like') => Promise<void>;
    saveLook: (id: string) => Promise<void>;
    deleteLook: (id: string) => Promise<void>;
  };
}

const AppContext = createContext<AppState | undefined>(undefined);



// Using picsum.photos for better CORS support when fetching images for AI processing
const getProductImage = (i: number, category: Category) => {
  return "";
};

const MOCK_PRODUCTS: Product[] = Array.from({ length: 40 }).map((_, i) => {
  const category = i % 4 === 0 ? Category.TOPS : (i % 4 === 1 ? Category.BOTTOMS : (i % 4 === 2 ? Category.SHOES : Category.ACCESSORIES));
  return {
    id: `p-${i}`,
    title: i % 2 === 0 ? `Футболка Basic №${i}` : `Джинсы Relaxed №${i}`,
    price: 1500 + (i * 100),
    currency: 'RUB',
    gender: i % 3 === 0 ? Gender.MALE : (i % 3 === 1 ? Gender.FEMALE : Gender.UNISEX),
    category,
    sizes: ['S', 'M', 'L', 'XL'],
    images: [getProductImage(i, category)],
    storeId: `s-${i % 5}`,
    availability: true,
    isCatalog: true,
  };
});

const MOCK_LOOKS: Look[] = Array.from({ length: 12 }).map((_, i) => ({
  id: `l-${i}`,
  userId: `u-${i % 3}`,
  title: `Стильный образ ${i + 1}`,
  items: [`p-${i}`, `p-${(i + 5) % 40}`],
  resultImageUrl: "",
  isPublic: true,
  likes: Math.floor(Math.random() * 50),
  comments: Math.floor(Math.random() * 10),
  createdAt: new Date(),
  authorName: ['alex_fit', 'marina_style', 'ksenia_vogue'][i % 3],
  authorAvatar: `https://i.pravatar.cc/150?u=${i % 3}`,
}));

function safeParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function clampArray<T>(arr: T[], max: number): T[] {
  if (arr.length <= max) return arr;
  return arr.slice(0, max);
}
async function urlToDataUrlIfMock(url: string): Promise<string> {
  if (!url) return url;
  if (url.startsWith('data:')) return url;

  // Convert local mock images into data URLs so backend doesn't fetch protected staging assets (BasicAuth -> 401)
  // We treat any URL containing "/mock/" as a mock asset.
  if (!url.includes('/mock/')) return url;

  // If url is absolute, convert to relative path to use current browser auth/session.
  let fetchUrl = url;
  try {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      const u = new URL(url);
      fetchUrl = u.pathname + (u.search || '');
    }
  } catch {
    // ignore
  }

  const res = await fetch(fetchUrl);
  if (!res.ok) throw new Error(`Failed to fetch mock image: ${res.status} ${fetchUrl}`);
  const blob = await res.blob();

  return await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error('FileReader failed'));
    r.readAsDataURL(blob);
  });
}

const STORAGE_KEY = "toptry_state_v1";

const TOPTRY_CLIENT_STORAGE_KEYS = [
  STORAGE_KEY,
  "toptry-state",
  "toptry_state",
  "toptry_state_v1",
];

function clearTopTryClientStorage() {
  if (typeof window === "undefined") return;

  for (const key of TOPTRY_CLIENT_STORAGE_KEYS) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }
}



export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useEffect(() => {
    window.__toptryClientLog?.("app_provider_mounted");
  }, []);

  const [user, setUser] = useState<User | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [wardrobe, setWardrobe] = useState<WardrobeItem[]>([]);
  const [looks, setLooks] = useState<Look[]>([]);
  const [homeLayout, setHomeLayout] = useState<HomeLayout>(HomeLayout.DASHBOARD);
  const [loading, setLoading] = useState(true);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  // Hydrate non-auth UI state from localStorage.
  // Backend /api/auth/me is the only source of truth for user session.
  // Do NOT restore saved.user here: otherwise stale localStorage can create phantom-login.
  useEffect(() => {
    const saved = safeParse<{ user: User | null; wardrobe: WardrobeItem[]; looks: Look[]; homeLayout: HomeLayout }>(
      typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null
    );

    if (saved) {
      setWardrobe(Array.isArray(saved.wardrobe) ? saved.wardrobe : []);

      const restoredLooks = (Array.isArray(saved.looks) ? saved.looks : []).map((l: any) => ({
        ...l,
        createdAt: l?.createdAt ? new Date(l.createdAt) : new Date(),
      }));
      setLooks(restoredLooks.length ? restoredLooks : []);

      setHomeLayout(saved.homeLayout || HomeLayout.DASHBOARD);
    }

    const t = setTimeout(() => setLoading(false), 450);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        console.info('[toptry][catalog] loading initial products');
        window.__toptryClientLog?.("catalog_initial_fetch_start");

        const resp = await fetch('/api/catalog/products', { credentials: 'include' });
        window.__toptryClientLog?.("catalog_initial_fetch_response", {
          status: resp.status,
          contentType: resp.headers.get('content-type') || '',
        });
        const contentType = resp.headers.get('content-type') || '';

        if (!resp.ok) {
          console.error('[toptry][catalog] initial products failed', {
            status: resp.status,
            contentType,
          });
          return;
        }

        if (contentType.includes('text/html')) {
          const html = await resp.text().catch(() => '');
          console.error('[toptry][catalog] initial products returned HTML instead of JSON', {
            status: resp.status,
            contentType,
            prefix: html.slice(0, 120),
          });
          return;
        }

        const data = await resp.json().catch((err) => {
          console.error('[toptry][catalog] initial products JSON parse failed', err);
          return null;
        });

        const items = Array.isArray(data?.products) ? data.products : [];
        console.info('[toptry][catalog] initial products loaded', {
          count: items.length,
          total: data?.total,
          meta: data?.meta,
        });

        setProducts(items);
      } catch (err) {
        console.error('[toptry][catalog] initial products request crashed', err);
      }
    })();
  }, []);

  // Restore server session (JWT in httpOnly cookie).
  // Important: backend is the source of truth. If /api/auth/me returns user:null,
  // clear stale local user from localStorage/state so the app does not look logged in.
  useEffect(() => {
    (async () => {
      try {
        window.__toptryClientLog?.("auth_me_fetch_start");
        const resp = await fetch('/api/auth/me', { credentials: 'include' });
        window.__toptryClientLog?.("auth_me_fetch_response", {
          status: resp.status,
          contentType: resp.headers.get('content-type') || '',
        });

        if (!resp.ok) {
          if (resp.status === 401) {
            clearTopTryClientStorage();
            setUser(null);
            setWardrobe([]);
            setLooks([]);
          }
          return;
        }

        const data = await resp.json().catch(() => null);

        if (!data?.user) {
          clearTopTryClientStorage();
          setUser(null);
          setWardrobe([]);
          setLooks([]);
          return;
        }

        const u = data.user;
        setUser((prev) => ({
          id: u.id,
          email: u.email || undefined,
          name: u.username || undefined,
          username: u.username || undefined,
          phone: u.phone || prev?.phone || '',
          avatarUrl: u.avatarUrl || undefined,
          selfieUrl: prev?.selfieUrl,
          sizeTop: u.sizeTop || undefined,
          sizeBottom: u.sizeBottom || undefined,
          sizeShoes: u.sizeShoes || undefined,
          catalogGenderPreference: u.catalogGenderPreference || undefined,
          tier: prev?.tier || SubscriptionTier.FREE,
          limits: prev?.limits || { hdTryOnRemaining: 5, looksRemaining: 10 },
          isPublic: !!u.isPublic,
          isAdmin: !!u.isAdmin,
        }));
      } catch (err) {
        // Network errors should not forcibly log the user out.
        console.error('[toptry][auth] /api/auth/me request crashed', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Always sync wardrobe from backend for authenticated user
  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        const resp = await fetch(`/api/wardrobe/list`, { credentials: 'include' });
        if (!resp.ok) return;
        const data = await resp.json().catch(() => null);
        const items = Array.isArray(data?.items) ? data.items : [];
        setWardrobe(
          items
            .map((raw: any) => ({
              ...raw,
              addedAt: raw?.addedAt ? new Date(raw.addedAt) : new Date(),
            }))
            .sort((a: any, b: any) =>
              new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime()
            )
        );
      } catch {
        // ignore
      }
    })();
  }, [user?.id]);

  // Always sync my looks from backend for authenticated user
  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        const resp = await fetch('/api/looks/my', { credentials: 'include' });
        if (!resp.ok) return;
        const data = await resp.json().catch(() => null);
        const serverLooks = Array.isArray(data?.looks) ? data.looks : [];
        setLooks(serverLooks.map((l: any) => ({ ...l, createdAt: new Date(l.createdAt) })));
      } catch {
        // ignore
      }
    })();
  }, [user?.id]);

  // Persist state (but avoid blowing up localStorage with huge base64 images)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Keep at most 10 looks in storage; if they include large data URLs, clamp harder.
    const looksForStorage = clampArray(
      looks,
      looks.some((l) => typeof l.resultImageUrl === 'string' && l.resultImageUrl.startsWith('data:')) ? 5 : 10
    );

    const payload = {
      user,
      wardrobe: clampArray(wardrobe, 200),
      looks: looksForStorage,
      homeLayout,
    };

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      // If storage is full, fallback to storing minimal state without images.
      try {
        const compactLooks = looksForStorage.map((l) => ({ ...l, resultImageUrl: (typeof l.resultImageUrl === "string" && l.resultImageUrl.startsWith("data:")) ? "" : (l.resultImageUrl || "") }));
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...payload, looks: compactLooks }));
      } catch {
        // ignore
      }
    }
  }, [user, wardrobe, looks, homeLayout]);

  const actions = useMemo(() => ({
    startPhoneAuth: async (phone: string) => {
  let r: Response;

  try {
    r = await fetch(`/api/auth/phone/start?t=${Date.now()}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      cache: 'no-store',
      body: JSON.stringify({ phone })
    });
  } catch (e: any) {
    console.warn('[auth] phone start failed before response', e);
    throw new Error('Не удалось отправить запрос. Сбросьте сессию или откройте сайт в обычном браузере.');
  }

  let text = '';
  try {
    text = await r.text();
  } catch {}

  let j: any = {};
  try {
    j = text ? JSON.parse(text) : {};
  } catch {}

  if (!r.ok) {
    const msg =
      j?.error ||
      (r.status === 429 ? 'Код уже отправлен, подождите немного' : null) ||
      `HTTP ${r.status}`;

    const err: any = new Error(msg);
    err.retryAfterSec = j?.retryAfterSec;
    err.status = r.status;

    throw err;
  }

  return j;
},

    verifyPhoneAuth: async (phone: string, code: string, referralCode?: string) => {
      let resp: Response;
      try {
        const body = new URLSearchParams({ phone, code });
        if (referralCode) body.set('referralCode', referralCode);
        resp = await fetch('/api/auth/phone/verify', {
          method: 'POST',
          credentials: 'include',
          body,
        });
      } catch (e: any) {
        throw new Error('Не удалось связаться с сервером. Попробуйте обновить страницу или открыть сайт в новой вкладке.');
      }

      const data = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        throw new Error(data?.error || 'Неверный код');
      }

      const u = data?.user;
      if (!u?.id) throw new Error('Сервер не вернул пользователя');

      setUser({
        id: u.id,
        email: u.email || undefined,
        name: u.username || undefined,
        username: u.username || undefined,
        phone: u.phone || phone,
        avatarUrl: u.avatarUrl || undefined,
        selfieUrl: undefined,
        sizeTop: u.sizeTop || undefined,
        sizeBottom: u.sizeBottom || undefined,
        sizeShoes: u.sizeShoes || undefined,
        tier: SubscriptionTier.FREE,
        limits: { hdTryOnRemaining: 5, looksRemaining: 10 },
        isPublic: u.isPublic ?? false,
        isAdmin: !!u.isAdmin,
      });

      return data;
    },
login: async (emailOrUsername: string, password: string) => {
  console.log('[auth] login start');

  const resp = await fetch('/api/auth/login', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ emailOrUsername, password }),
  });

  console.log('[auth] login response', resp.status);

  const raw = await resp.text();
  console.log('[auth] login body len', raw?.length ?? 0);

  let data: any = {};
  try {
    data = raw ? JSON.parse(raw) : {};
  } catch (e) {
    console.error('[auth] login JSON parse failed. First 300 chars:', raw?.slice(0, 300));
    throw new Error(`Login: invalid JSON (status ${resp.status})`);
  }

  if (!resp.ok) {
    console.error('[auth] login not ok', data);
    throw new Error(data?.error || `Login failed (${resp.status})`);
  }

  const u = data.user;
  if (!u?.id) throw new Error('Login: server did not return user');

  console.log('[auth] login setUser');

  setUser((prev) => ({
    id: u.id,
    email: u.email,
    name: prev?.name || u.username,
    username: u.username,
    phone: prev?.phone || '',
    avatarUrl: u.avatarUrl || prev?.avatarUrl,
    selfieUrl: prev?.selfieUrl,
    sizeTop: u.sizeTop || prev?.sizeTop,
    sizeBottom: u.sizeBottom || prev?.sizeBottom,
    sizeShoes: u.sizeShoes || prev?.sizeShoes,
    tier: prev?.tier || SubscriptionTier.FREE,
    limits: prev?.limits || { hdTryOnRemaining: 5, looksRemaining: 10 },
    isPublic: u.isPublic ?? true,
    isAdmin: !!u.isAdmin,
  }));

  console.log('[auth] login done');
},

register: async (email: string, username: string, password: string) => {
  console.log('[auth] register start');

  const resp = await fetch('/api/auth/register', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, username, password }),
  });

  console.log('[auth] register response', resp.status);

  const raw = await resp.text();
  console.log('[auth] register body len', raw?.length ?? 0);

  let data: any = {};
  try {
    data = raw ? JSON.parse(raw) : {};
  } catch (e) {
    console.error('[auth] register JSON parse failed. First 300 chars:', raw?.slice(0, 300));
    throw new Error(`Register: invalid JSON (status ${resp.status})`);
  }

  if (!resp.ok) {
    console.error('[auth] register not ok', data);
    throw new Error(data?.error || `Registration failed (${resp.status})`);
  }

  const u = data.user;
  if (!u?.id) throw new Error('Register: server did not return user');

  console.log('[auth] register setUser');

  setUser((prev) => ({
    id: u.id,
    email: u.email,
    name: prev?.name || u.username,
    username: u.username,
    phone: prev?.phone || '',
    avatarUrl: u.avatarUrl || prev?.avatarUrl,
    selfieUrl: prev?.selfieUrl,
    sizeTop: u.sizeTop || prev?.sizeTop,
    sizeBottom: u.sizeBottom || prev?.sizeBottom,
    sizeShoes: u.sizeShoes || prev?.sizeShoes,
    tier: prev?.tier || SubscriptionTier.FREE,
    limits: prev?.limits || { hdTryOnRemaining: 5, looksRemaining: 10 },
    isPublic: u.isPublic ?? true,
    isAdmin: !!u.isAdmin,
  }));

  console.log('[auth] register done');
},

    updateProfileSizes: async (sizeTop: string, sizeBottom: string, sizeShoes: string, catalogGenderPreference?: string) => {
      const resp = await fetch('/api/profile/update', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sizeTop, sizeBottom, sizeShoes, catalogGenderPreference: catalogGenderPreference || user?.catalogGenderPreference || 'ALL' }),
      });

      const data = await resp.json().catch(() => ({}));

      if (resp.status === 401) {
        setUser(null);
        setWardrobe([]);
        setLooks([]);
        throw new Error('SESSION_EXPIRED');
      }

      if (!resp.ok) {
        throw new Error(data?.error || 'Profile update failed');
      }

      setUser((prev) =>
        prev
          ? {
              ...prev,
              sizeTop: data?.user?.sizeTop || undefined,
              sizeBottom: data?.user?.sizeBottom || undefined,
              sizeShoes: data?.user?.sizeShoes || undefined,
              catalogGenderPreference: data?.user?.catalogGenderPreference || undefined,
              publicSlug: data?.user?.publicSlug || undefined,
              publicDisplayName: data?.user?.publicDisplayName || undefined,
              publicBio: data?.user?.publicBio || undefined,
              publicSocialUrl: data?.user?.publicSocialUrl || undefined,
            }
          : prev
      );
    },

    updatePublicProfile: async (publicSlug: string, publicDisplayName: string, publicBio: string, publicSocialUrl: string) => {
      const resp = await fetch('/api/profile/update', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sizeTop: user?.sizeTop || '',
          sizeBottom: user?.sizeBottom || '',
          sizeShoes: user?.sizeShoes || '',
          catalogGenderPreference: user?.catalogGenderPreference || 'ALL',
          publicSlug,
          publicDisplayName,
          publicBio,
          publicSocialUrl,
        }),
      });

      const data = await resp.json().catch(() => ({}));

      if (resp.status === 401) {
        setUser(null);
        setWardrobe([]);
        setLooks([]);
        throw new Error('SESSION_EXPIRED');
      }

      if (!resp.ok) {
        throw new Error(data?.error || 'Public profile update failed');
      }

      setUser((prev) =>
        prev
          ? {
              ...prev,
              publicSlug: data?.user?.publicSlug || undefined,
              publicDisplayName: data?.user?.publicDisplayName || undefined,
              publicBio: data?.user?.publicBio || undefined,
              publicSocialUrl: data?.user?.publicSocialUrl || undefined,
            }
          : prev
      );
    },

    logout: async () => {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => null);
      clearTopTryClientStorage();
      setUser(null);
      setWardrobe([]);
      setLooks([]);
    },

    resetSession: () => {
      clearTopTryClientStorage();
      setUser(null);
      setWardrobe([]);
      setLooks([]);
    },

    refreshMe: async () => {
      try {
        window.__toptryClientLog?.("auth_me_fetch_start");
        const resp = await fetch('/api/auth/me', { credentials: 'include' });
        window.__toptryClientLog?.("auth_me_fetch_response", {
          status: resp.status,
          contentType: resp.headers.get('content-type') || '',
        });

        if (!resp.ok) {
          if (resp.status === 401) {
            clearTopTryClientStorage();
            setUser(null);
            setWardrobe([]);
            setLooks([]);
          }
          return null;
        }

        const data = await resp.json().catch(() => null);

        if (!data?.user) {
          setUser(null);
          setWardrobe([]);
          setLooks([]);
          return null;
        }

        const u = data.user;
        const nextUser = {
          id: u.id,
          email: u.email || undefined,
          name: u.username || undefined,
          username: u.username || undefined,
          phone: u.phone || '',
          avatarUrl: u.avatarUrl || undefined,
          selfieUrl: user?.selfieUrl,
          sizeTop: u.sizeTop || undefined,
          sizeBottom: u.sizeBottom || undefined,
          sizeShoes: u.sizeShoes || undefined,
          publicSlug: u.publicSlug || undefined,
          publicDisplayName: u.publicDisplayName || undefined,
          publicBio: u.publicBio || undefined,
          publicSocialUrl: u.publicSocialUrl || undefined,
          tier: user?.tier || SubscriptionTier.FREE,
          limits: user?.limits || { hdTryOnRemaining: 5, looksRemaining: 10 },
          isPublic: !!u.isPublic,
          isAdmin: !!u.isAdmin,
        };

        setUser(nextUser);
        return nextUser;
      } catch {
        // Network errors should not forcibly log the user out.
        return user || null;
      }
    },
    toggleHomeLayout: () => setHomeLayout(prev => 
      prev === HomeLayout.DASHBOARD ? HomeLayout.FEED : HomeLayout.DASHBOARD
    ),
    addToWardrobe: async (product: Product) => {
      const userId = user?.id || 'demo-user-id';

      if (user?.id) {
        try {
          const resp = await fetch('/api/wardrobe/save-catalog', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(product),
          });
          const data = await resp.json().catch(() => ({}));
          if (resp.ok && data?.item) {
            const saved = data.item;
            setWardrobe(prev => {
              const withoutSameSavedId = prev.filter(i => i.id !== saved.id);
              const alreadyPresent = withoutSameSavedId.some((i: any) => {
                if (!(i?.isCatalog || i?.sourceType === 'catalog')) return false;
                if (saved?.affiliateUrl && i?.affiliateUrl === saved.affiliateUrl) return true;
                if (saved?.productUrl && i?.productUrl === saved.productUrl) return true;
                if (saved?.images?.[0] && i?.images?.[0] === saved.images[0]) return true;
                return false;
              });

              if (alreadyPresent) {
                return withoutSameSavedId;
              }

              return [
                { ...saved, addedAt: saved?.addedAt ? new Date(saved.addedAt) : new Date() },
                ...withoutSameSavedId
              ];
            });
            return;
          }
        } catch {}
      }

      setWardrobe(prev => {
        if (prev.some(item => item.id === product.id)) return prev;
        const newItem: WardrobeItem = {
          ...product,
          userId: userId,
          addedAt: new Date(),
          isCatalog: true,
          sourceType: 'catalog',
        };
        return [newItem, ...prev];
      });
    },
    addMultipleToWardrobe: (products: Product[]) => {
      const userId = user?.id || 'demo-user-id';
      const newItems = products.map(p => ({
        ...p,
        userId: userId,
        addedAt: new Date(),
        isCatalog: false,
        sourceType: 'own',
      }));
      setWardrobe(prev => [...newItems, ...prev]);
    },
    upsertWardrobeItem: (item: WardrobeItem) => {
      setWardrobe((prev) => {
        const next = prev.filter((p) => p.id !== item.id);
        return [
          {
            ...item,
            addedAt: (item as any)?.addedAt
              ? new Date((item as any).addedAt)
              : new Date(),
          },
          ...next,
        ];
      });
    },
    removeFromWardrobe: async (id: string) => {
      try {
        const resp = await fetch(`/api/wardrobe/item/${encodeURIComponent(id)}`, {
          method: 'DELETE',
          credentials: 'include',
        });

        const data = await resp.json().catch(() => ({}));

        if (!resp.ok) {
          throw new Error(data?.error || 'Delete failed');
        }

        setWardrobe(prev => prev.filter(i => i.id !== id));
      } catch (e) {
        console.error('[wardrobe] delete failed', e);
      }
    },
    createLook: async (selectedItems: WardrobeItem[]) => {
      const selfieUrl = user?.selfieUrl || user?.avatarUrl;
      if (!user || !selfieUrl) {
        throw new Error('CREATE_LOOK_PRECHECK: missing user or selfie');
      }
      if (!selectedItems?.length) {
        throw new Error('CREATE_LOOK_PRECHECK: no selected items');
      }

      setAiError(null);
      setAiBusy(true);

      try {
        console.log('[createLook] start', {
          userId: user.id,
          selfieUrl,
          selectedCount: selectedItems.length,
          selectedIds: selectedItems.map((i) => i.id),
        });

        // Keep the visual reference and its metadata in the same record. Filtering
        // URLs separately used to shift indexes when one selected item had no image.
        const selectedItemRefs = selectedItems.map((item) => {
          const maybe =
            (item as any).cutoutUrl ||
            (item as any).originalUrl ||
            (item as any).imageUrl ||
            (item.images && item.images[0]);

          return {
            item,
            url: typeof maybe === 'string' ? withApiOrigin(maybe) : '',
          };
        });

        const missingImageItem = selectedItemRefs.find(({ url }) => !url);
        if (missingImageItem) {
          throw new Error(`CREATE_LOOK_IMAGES: no usable image for ${missingImageItem.item.title || 'selected item'}`);
        }

        console.log('[createLook] item refs', selectedItemRefs.map(({ item, url }, index) => ({
          index,
          id: item.id,
          title: item.title,
          category: item.category,
          imagePrefix: String(url).slice(0, 80),
        })));

        const itemImageUrls = await Promise.all(
          selectedItemRefs.map(async ({ url, item }, idx) => {
            try {
              const prepared = await urlToDataUrlIfMock(url);
              console.log('[createLook] prepared item image', {
                idx,
                title: item.title,
                originalPrefix: String(url).slice(0, 80),
                preparedPrefix: String(prepared).slice(0, 80),
              });
              return prepared;
            } catch (err: any) {
              console.error('[createLook] prepare item image failed', idx, item.title, url, err);
              throw new Error(`CREATE_LOOK_PREPARE_IMAGE_${idx}: ${err?.message || err}`);
            }
          })
        );

        const itemIds = selectedItemRefs.map(({ item }) => item.id);
        const sourceItems = selectedItemRefs.map(({ item }) => ({
          id: item.id,
          title: item.title,
          price: item.price || 0,
          currency: item.currency || 'RUB',
          category: item.category,
          gender: item.gender,
          images: Array.isArray(item.images) ? item.images : [],
          brand: (item as any).brand || undefined,
          storeId: item.storeId,
          storeName: (item as any).storeName || undefined,
          isCatalog: !!item.isCatalog,
          affiliateUrl: (item as any).affiliateUrl || undefined,
          productUrl: (item as any).productUrl || undefined,
        }));

        const tryonItems = selectedItemRefs.map(({ item }) => ({
          id: item.id,
          title: item.title,
          category: item.category,
          taxonomyGroup: (item as any).taxonomyGroup || undefined,
          taxonomySubgroup: (item as any).taxonomySubgroup || undefined,
          gender: item.gender,
          brand: (item as any).brand || undefined,
          isCatalog: !!item.isCatalog,
        }));

        const priceBuyNowRUB = selectedItems
          .filter((i) => i.isCatalog)
          .reduce((s, i) => s + (i.price || 0), 0);

        const payload = {
          selfieDataUrl: withApiOrigin(selfieUrl),
          itemImageUrls,
          itemIds,
          sourceItems,
          tryonItems,
          aspectRatio: '3:4',
          qualityMode: 'quality',
          priceBuyNowRUB,
        };

        console.log('[createLook] before fetch', {
          selfiePrefix: String(payload.selfieDataUrl).slice(0, 80),
          itemCount: payload.itemImageUrls.length,
          firstItemPrefix: payload.itemImageUrls[0] ? String(payload.itemImageUrls[0]).slice(0, 80) : null,
        });

        let resp: Response;
        try {
          resp = await fetch('/api/looks/create', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
        } catch (err: any) {
          console.error('[createLook] fetch failed before response', err);
          throw new Error(`CREATE_LOOK_FETCH: ${err?.message || err}`);
        }

        const raw = await resp.text();
        console.log('[createLook] response status', resp.status, raw.slice(0, 300));

        let data: any = {};
        try {
          data = raw ? JSON.parse(raw) : {};
        } catch {
          data = {};
        }

        if (!resp.ok) {
          if (resp.status === 401) {
            throw new Error('AUTH_REQUIRED');
          }

          if (resp.status === 429 && data?.code === 'LOOK_GENERATION_LIMIT_REACHED') {
            const limitErr: any = new Error(data?.error || 'Лимит генераций исчерпан');
            limitErr.code = data.code;
            limitErr.limitType = data?.limitType || null;
            limitErr.usage = data?.usage || null;
            throw limitErr;
          }

          throw new Error(data?.error || raw || `AI server error (${resp.status})`);
        }

        const look = data?.look;
        if (!look) throw new Error('CREATE_LOOK_RESPONSE: server did not return look');

        const newLook: Look = { ...look, createdAt: new Date(look.createdAt) };
        setLooks((prev) => [newLook, ...prev]);
        setUser((u) =>
          u
            ? {
                ...u,
                limits: {
                  ...u.limits,
                  looksRemaining: Math.max(0, u.limits.looksRemaining - 1),
                  hdTryOnRemaining: Math.max(0, u.limits.hdTryOnRemaining - 1),
                },
              }
            : null
        );
        return newLook.id;
      } catch (e: any) {
        console.error('[createLook] final error', e);
        setAiError(e?.message || 'Ошибка генерации образа');
        throw e;
      } finally {
        setAiBusy(false);
      }
    },
    setSelfie: (url: string) => {
      setUser(prev => {
        if (prev) return { ...prev, selfieUrl: url };
        return {
          id: 'u-current',
          name: 'Стильный Гость',
          username: 'guest_looker',
          phone: '+7 (000) 000-00-00',
          avatarUrl: 'https://i.pravatar.cc/150?u=guest',
          selfieUrl: url,
          sizeTop: undefined,
          sizeBottom: undefined,
          sizeShoes: undefined,
          tier: SubscriptionTier.FREE,
          limits: { hdTryOnRemaining: 5, looksRemaining: 10 },
          isPublic: false,
        };
      });
    },
    likeLook: async (id: string) => {
      try {
        const resp = await fetch(`/api/looks/${encodeURIComponent(id)}/like`, { method: 'POST' , credentials: 'include' });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(data?.error || 'Like failed');
        setLooks((prev) => prev.map((l) => (l.id === id ? { ...l, likes: data.likes } : l)));
      } catch {
        // ignore
      }
    },

    reactToLook: async (id: string, reaction: 'like' = 'like') => {
      if (reaction !== 'like') return;

      try {
        const resp = await fetch(`/api/looks/${encodeURIComponent(id)}/like`, {
          method: 'POST',
          credentials: 'include',
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(data?.error || 'Like failed');

        setLooks((prev) =>
          prev.map((l) =>
            l.id === id
              ? {
                  ...l,
                  likes: data?.likes ?? l.likes,
                  viewerLiked: true,
                }
              : l
          )
        );
      } catch {
        // ignore
      }
    },

    saveLook: async (id: string) => {
      try {
        const resp = await fetch(`/api/looks/${encodeURIComponent(id)}/save`, {
          method: 'POST',
          credentials: 'include',
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(data?.error || 'Save failed');

        setLooks((prev) =>
          prev.map((l) =>
            l.id === id
              ? {
                  ...l,
                  saves: data?.saves ?? ((l.saves || 0) + 1),
                  viewerSaved: true,
                }
              : l
          )
        );
      } catch {
        // ignore
      }
    },

    deleteLook: async (id: string) => {
      const resp = await fetch(`/api/looks/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      const data = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        throw new Error(data?.error || 'Не удалось удалить образ');
      }

      setLooks((prev) => prev.filter((l) => String(l.id) !== String(id)));
    },
  }), [user, looks, wardrobe, homeLayout]);
  
  return (
    <AppContext.Provider value={{
      user, products, wardrobe, looks, homeLayout, loading, aiBusy, aiError, actions }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppState = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useAppState must be used within AppProvider');
  return context;
};
